// Favorite emoji: the emoji someone stars in the picker, kept with their account (`favoriteEmoji` in /api/me/settings),
// so every device and client shows the same ones, at the top of the picker in the order they were starred. An entry is
// a Unicode emoji or a server emoji's token ("<:name:id>"); a server emoji shows only in its own server (clients drop
// the others where they can't be drawn, as with frequently used). The cases every client runs:
// shared/cases/favorite-emoji.json.
import { parseEmojiToken } from "./emoji.ts";

/** Five rows of the picker's eight. */
export const MAX_FAVORITE_EMOJI = 40;

/**
 * What makes two entries the same favorite: a server emoji's id (it survives a rename), else the emoji without its
 * emoji presentation selector (U+FE0F), so "❤" and "❤️" are one favorite.
 */
export const favoriteKey = (emoji: string) => parseEmojiToken(emoji)?.id ?? emoji.replaceAll("\uFE0F", "");

/** Code points that may only join or modify another: the joiner, variation selectors, the keycap mark, tags. */
const isModifier = (cp: number) => cp === 0x200d || cp === 0xfe0e || cp === 0xfe0f || cp === 0x20e3 || (cp >= 0xe0020 && cp <= 0xe007f);

/** Code points an emoji is drawn from (the blocks Unicode's emoji come from, without spaces, formats or braille). */
function isEmojiBase(cp: number): boolean {
  return (
    cp === 0xa9 || cp === 0xae || cp === 0x203c || cp === 0x2049 ||
    (cp >= 0x2100 && cp <= 0x2bff && !(cp >= 0x2800 && cp <= 0x28ff)) ||
    cp === 0x3030 || cp === 0x303d || cp === 0x3297 || cp === 0x3299 ||
    (cp >= 0x1f000 && cp <= 0x1faff)
  );
}

/**
 * Whether a string can be a favorite: a server emoji token, or 1–32 UTF-16 units of well-formed text made only of
 * emoji characters (isEmojiBase), joiners and modifiers (isModifier), with at least one emoji character, or a keycap
 * ("#", "*" or a digit, then U+20E3). Lenient within those blocks: it keeps junk out of the stored list, not every
 * non-emoji symbol.
 */
export function isFavoriteEmoji(s: string): boolean {
  if (parseEmojiToken(s)) return true;
  if (s.length < 1 || s.length > 32) return false;
  const keycap = s.includes("\u20E3");
  let base = false;
  for (let i = 0; i < s.length; i++) {
    let cp = s.charCodeAt(i);
    if (cp >= 0xd800 && cp <= 0xdbff) {
      const lo = s.charCodeAt(i + 1);
      if (!(lo >= 0xdc00 && lo <= 0xdfff)) return false;
      cp = 0x10000 + ((cp - 0xd800) << 10) + (lo - 0xdc00);
      i++;
    } else if (cp >= 0xdc00 && cp <= 0xdfff) return false;
    if (keycap && (cp === 0x23 || cp === 0x2a || (cp >= 0x30 && cp <= 0x39))) base = true;
    else if (isEmojiBase(cp)) base = true;
    else if (!isModifier(cp)) return false;
  }
  return base;
}

/** A stored or sent list, checked: null unless it's a list of at most MAX_FAVORITE_EMOJI favorites; repeats are dropped. */
export function cleanFavoriteEmoji(v: unknown): string[] | null {
  if (!Array.isArray(v) || !v.every((s): s is string => typeof s === "string" && isFavoriteEmoji(s))) return null;
  const seen = new Set<string>();
  const out = v.filter((s) => !seen.has(favoriteKey(s)) && !!seen.add(favoriteKey(s)));
  return out.length <= MAX_FAVORITE_EMOJI ? out : null;
}

/**
 * A stored list read leniently: the entries that are favorites, repeats dropped, the first MAX_FAVORITE_EMOJI. One
 * entry a newer, stricter rule refuses doesn't cost the rest.
 */
export function keepFavoriteEmoji(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of v) {
    if (typeof s !== "string" || !isFavoriteEmoji(s) || seen.has(favoriteKey(s))) continue;
    seen.add(favoriteKey(s));
    out.push(s);
    if (out.length === MAX_FAVORITE_EMOJI) break;
  }
  return out;
}

export const isFavorite = (list: readonly string[], emoji: string) => list.some((f) => favoriteKey(f) === favoriteKey(emoji));

/** Stars or unstars one: a favorite comes off, anything else goes on the end. A full list stays as it is. */
export function toggleFavoriteEmoji(list: readonly string[], emoji: string): string[] {
  if (isFavorite(list, emoji)) return list.filter((f) => favoriteKey(f) !== favoriteKey(emoji));
  return list.length < MAX_FAVORITE_EMOJI ? [...list, emoji] : [...list];
}

/** One change to the list, as a client sends it (PUT /api/me/settings `favoriteEmojiAdd` / `favoriteEmojiRemove`). */
export interface FavoriteChange {
  add?: string;
  remove?: string;
}

/**
 * Applies one change to the stored list: a remove takes that favorite off, an add puts it on the end unless it's
 * already there or the list is full. Clients send changes, not lists, so a device with an old copy can't undo what
 * another device starred.
 */
export function applyFavoriteChange(list: readonly string[], change: FavoriteChange): string[] {
  let out = change.remove === undefined ? [...list] : list.filter((f) => favoriteKey(f) !== favoriteKey(change.remove!));
  if (change.add !== undefined && !isFavorite(out, change.add) && out.length < MAX_FAVORITE_EMOJI) out = [...out, change.add];
  return out;
}
